import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect } from "react";
import { Flag, Trophy, ArrowRight, ShieldAlert } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { trackEvent } from "@/lib/audit";
import { RouteTopBar } from "@/components/layout/RouteTopBar";
import { QrEntryBadge } from "@/components/qr/QrEntryBadge";
import { setStoredLang, type Lang } from "@/lib/i18n";
import { useLanguage } from "@/hooks/useLanguage";
import {
  HeroBlitz,
  UrgencyCountdown,
  LiveActivityBar,
  SocialProofPopup,
  ReferralBlock,
  TrustBlock,
} from "@/components/conversion/WCBlitz";
import { GuestMicroPrediction } from "@/components/predictions/GuestMicroPrediction";
import { WhatsAppShareButton } from "@/components/whatsappViral/WhatsAppShareButton";
import { PREVIEW_MODE } from "@/lib/previewMode";

export const Route = createFileRoute("/welcome")({
  head: () => ({
    meta: [
      { title: "VerseSport — Inteligencia Deportiva en Tiempo Real" },
      { name: "description", content: "Plataforma global de segunda pantalla con telemetría, IA, predicciones, recompensas y analítica comercial en tiempo real." },
      { property: "og:title", content: "VerseSport — Inteligencia Deportiva en Tiempo Real" },
      { property: "og:description", content: "Plataforma global de segunda pantalla con telemetría, IA, predicciones, recompensas y analítica comercial en tiempo real." },
      { name: "twitter:title", content: "VerseSport — Inteligencia Deportiva en Tiempo Real" },
      { name: "twitter:description", content: "Plataforma global de segunda pantalla con telemetría, IA, predicciones, recompensas y analítica comercial en tiempo real." },
    ],
  }),
  validateSearch: (search: Record<string, unknown>) => ({
    src: typeof search.src === "string" ? search.src : undefined,
    ref: typeof search.ref === "string" ? search.ref : undefined,
    tenant: typeof search.tenant === "string" ? search.tenant : undefined,
    lang: typeof search.lang === "string" ? search.lang : undefined,
    // P0-AUTH-10 — preserve the route the user was trying to reach so the
    // safety-net redirect below can return them there instead of dumping
    // them on Home on every refresh.
    from: typeof search.from === "string" ? search.from : undefined,
  }),
  component: WelcomePage,
});

function NationWarsDiscovery() {
  const { t } = useLanguage();
  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-start gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-amber-500/15 text-amber-500">
          <Trophy className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-black text-foreground">{t("welcome_nw_title")}</h2>
          <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
            {t("welcome_nw_body")}
          </p>
        </div>
      </div>
      <Link
        to="/nation-wars"
        onClick={() => void trackEvent("nation_wars_discovery_cta" as unknown as import("@/types").EventType, { surface: "welcome" })}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3 text-sm font-black text-primary-foreground shadow transition-transform hover:scale-[1.02]"
      >
        <Flag className="h-4 w-4" />
        {t("welcome_nw_cta")}
        <ArrowRight className="h-4 w-4" />
      </Link>
    </section>
  );
}

function WelcomePage() {
  const search = useSearch({ from: "/welcome" });
  const navigate = useNavigate();
  const { authReady, isAuthenticated } = useAuth();
  const { t } = useLanguage();

  // P0-AUTH-09 — safety net. If an authenticated user lands on /welcome
  // (e.g. because the auth watchdog briefly flipped ready with session=null
  // before INITIAL_SESSION arrived), bounce them back to where they came
  // from. P0-AUTH-10 — if `?from=` was preserved by the guard that sent us
  // here, restore the original route; otherwise default to Home.
  useEffect(() => {
    if (authReady && isAuthenticated) {
      const target = search.from && search.from.startsWith("/") && !search.from.startsWith("//")
        ? search.from
        : "/";
      void trackEvent("welcome_authed_redirect", { to: target });
      void navigate({ to: target, search: { demo: undefined } as never });
    }
  }, [authReady, isAuthenticated, navigate, search.from]);

  useEffect(() => {
    void trackEvent("welcome_dashboard_viewed", {});
  }, []);

  // F3 — QR / deep-link attribution capture. Apply ?lang= (es|en|pt)
  // and forward source / ref / tenant into the partner attribution
  // pipeline. Runs once per landing.
  useEffect(() => {
    const wanted = (search.lang ?? "").toLowerCase();
    const map: Record<string, Lang> = { es: "ES", en: "EN", pt: "PT" };
    if (map[wanted]) setStoredLang(map[wanted]);
    if (search.src || search.ref || search.tenant) {
      void trackEvent("qr_attribution_captured", {
        src: search.src ?? null,
        ref: search.ref ?? null,
        tenant: search.tenant ?? null,
      });
    }
  }, [search.lang, search.src, search.ref, search.tenant]);

  const onSecondary = () => {
    void navigate({ to: "/login", search: { from: undefined } });
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-background">
      <RouteTopBar fallback="/" />
      <div className="mx-auto w-full max-w-3xl px-3 py-6 sm:px-6 sm:py-10">
        <HeroBlitz />

        <div className="mt-4 space-y-3">
          <UrgencyCountdown />
          <div className="flex justify-center">
            <LiveActivityBar />
          </div>
        </div>

        <div className="mt-6">
          <TrustBlock />
        </div>

        <div className="mt-6">
          <NationWarsDiscovery />
        </div>

        {/* GO_LIVE_P0 — viral share above the fold for guests. */}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2 rounded-2xl border border-white/10 bg-white/5 p-3">
          <span className="text-[11px] font-bold text-muted-foreground">
            {t("welcome_invite_friends") || "Invita a tus amigos"}
          </span>
          <WhatsAppShareButton
            kind="nation"
            payload={{ nationCode: "ESP" }}
            source="welcome_hero"
            label="WhatsApp"
          />
        </div>

        {!isAuthenticated && (
          <div className="mt-6">
            <GuestMicroPrediction />
          </div>
        )}

        <div className="mt-6">
          <ReferralBlock />
        </div>


        {!PREVIEW_MODE && (
          <button
            type="button"
            onClick={onSecondary}
            className="mt-6 block w-full text-center text-xs font-bold text-muted-foreground hover:text-foreground"
          >
            {t("welcome_have_account")}
          </button>
        )}

        <Link
          to="/superadmin-access"
          onClick={() => void trackEvent("super_admin_entry_from_welcome" as unknown as import("@/types").EventType, { surface: "welcome" })}
          className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border border-[oklch(0.65_0.25_25)]/40 bg-[oklch(0.65_0.25_25)]/10 px-4 py-2 text-[11px] font-black uppercase tracking-widest text-[oklch(0.82_0.24_25)] transition hover:bg-[oklch(0.65_0.25_25)]/20"
        >
          <ShieldAlert className="h-3.5 w-3.5" />
          Super Admin
        </Link>

        <div className="mt-10 flex flex-col items-center gap-2 border-t border-white/10 pt-6 opacity-70">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {t("welcome_qr_scan")}
          </p>
          <QrEntryBadge
            source={search.src ?? "welcome"}
            ref={search.ref}
            tenant={search.tenant}
            size={58}
          />
        </div>
      </div>

      <SocialProofPopup />
    </main>
  );
}